import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import ComparisonVisual from "@/components/ComparisonVisual";
import CtaSection from "@/components/CtaSection";
import { IMAGES } from "@/data/site";

const REVIEW_ITEMS = [
    { title: "Quantity Review", text: "Billed quantities checked against drawings, BOQ and site measurements where available." },
    { title: "Measurement Review", text: "Measurement records examined for method, units, deductions and arithmetic." },
    { title: "BOQ Comparison", text: "Billed items compared line by line against the agreed BOQ and scope." },
    { title: "Rate Review", text: "Applied rates checked against agreed or quoted rates for each item." },
    { title: "Extra Item Review", text: "Extra and substituted items examined for basis, necessity and rate reasonableness." },
    { title: "Missing / Incomplete Work", text: "Work billed but not visibly complete on site, flagged with observations." },
    { title: "Supporting Documents", text: "Vouchers, measurement books and supporting records reviewed for consistency." },
];

export default function BoqBillReview() {
    return (
        <div>
            <Seo
                title="BOQ & Contractor Bill Review | The Sens Civil Works Consultancy"
                description="Independent review of contractor bills, quantities, measurements, BOQs, rates and extra items before you pay — Gorakhpur, Uttar Pradesh."
            />
            <PageHero
                overline="BOQ & Bill Verification"
                title="Check Your Contractor Bill Before You Pay"
                description="An independent technical review of quantities, measurements, rates and extra items — with documented observations you can use in your payment discussion."
            />

            <section className="bg-white py-20 lg:py-28" data-testid="bill-review-scope">
                <div className="mx-auto max-w-7xl px-6 lg:px-12">
                    <Reveal>
                        <p className="font-mono text-xs uppercase tracking-[0.25em] text-amber">Scope of Review</p>
                        <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-navy sm:text-4xl">
                            What Gets Checked
                        </h2>
                    </Reveal>
                    <div className="mt-12 grid grid-cols-1 gap-px border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:grid-cols-3">
                        {REVIEW_ITEMS.map((item, i) => (
                            <Reveal key={item.title} delay={Math.min(i * 0.05, 0.3)} className="bg-white">
                                <div className="h-full p-7">
                                    <CheckCircle2 size={20} className="text-amber" />
                                    <h3 className="mt-4 font-display text-base font-bold text-navy">{item.title}</h3>
                                    <p className="mt-2 text-sm leading-relaxed text-slate-600">{item.text}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal delay={0.15}>
                        <Link
                            to="/contact?service=Bill%20Checking"
                            className="mt-12 inline-flex items-center gap-2 bg-amber px-7 py-4 font-mono text-xs font-semibold uppercase tracking-[0.18em] text-white transition-colors duration-300 hover:bg-amber-hover"
                            data-testid="submit-bill-review-btn"
                        >
                            Submit Bill for Review <ArrowRight size={14} />
                        </Link>
                    </Reveal>
                </div>
            </section>

            <section className="border-t border-slate-200 bg-background py-20 lg:py-28" data-testid="bill-comparison">
                <div className="mx-auto max-w-7xl px-6 lg:px-12">
                    <Reveal>
                        <p className="font-mono text-xs uppercase tracking-[0.25em] text-amber">The Difference</p>
                        <h2 className="mt-4 max-w-2xl font-display text-3xl font-bold tracking-tight text-navy sm:text-4xl">
                            Contractor Submission vs Independent Review
                        </h2>
                    </Reveal>
                    <div className="mt-12">
                        <ComparisonVisual />
                    </div>
                </div>
            </section>

            <section className="border-t border-slate-200 bg-white py-20 lg:py-28">
                <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-6 lg:grid-cols-12 lg:px-12">
                    <div className="lg:col-span-6">
                        <Reveal>
                            <p className="font-mono text-xs uppercase tracking-[0.25em] text-amber">How It Works</p>
                            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-navy sm:text-4xl">
                                Simple, Documented, Independent
                            </h2>
                            <ul className="mt-8 space-y-4">
                                {[
                                    "Share the contractor bill, BOQ, agreement and any measurement records",
                                    "Scope and fee for the review are agreed up front",
                                    "Quantities, rates and extras are examined item by item",
                                    "You receive written observations — before you decide on payment",
                                ].map((item) => (
                                    <li key={item} className="flex items-start gap-3 border-b border-slate-200 pb-4 text-sm text-slate-700">
                                        <span className="mt-1.5 h-2 w-2 shrink-0 rotate-45 bg-amber" aria-hidden="true" />
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </Reveal>
                    </div>
                    <div className="lg:col-span-6">
                        <Reveal delay={0.1}>
                            <div className="tick-frame">
                                <img
                                    src={IMAGES.measuring.url}
                                    alt={IMAGES.measuring.alt}
                                    className="aspect-[4/3] w-full object-cover"
                                    loading="lazy"
                                    data-testid="boq-image"
                                />
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            <CtaSection
                overline="Before You Pay"
                title="Send the Bill for an Independent Check"
                description="A short review now can prevent a long dispute later. Share your bill and BOQ to begin."
            />
        </div>
    );
}
